<script setup>
import { useRoute, useRouter } from 'vue-router'
import HaiService from '@/services/quality/hai.service'
import { HaiCaseStatus } from '@/enums/quality/hai-case-status'
import IsolationDrawer from '@/views/quality/infection-control/isolation/IsolationDrawer.vue'
import { IsolationStatus } from '@/enums/quality/isolation-status'
import CaseHeaderCard from '@/views/quality/infection-control/hai/view/CaseHeaderCard.vue'
import ValidationFlags from '@/views/quality/infection-control/hai/view/ValidationFlags.vue'
import DeviceTimeline from '@/views/quality/infection-control/hai/view/DeviceTimeline.vue'
import CaseDetails from '@/views/quality/infection-control/hai/view/CaseDetails.vue'
import CriteriaChecklist from '@/views/quality/infection-control/hai/view/CriteriaChecklist.vue'
import ReviewerNotes from '@/views/quality/infection-control/hai/view/ReviewerNotes.vue'
import DecisionCard from '@/views/quality/infection-control/hai/view/DecisionCard.vue'
import MdroAlert from '@/views/quality/infection-control/hai/view/MdroAlert.vue'
import LinkedIsolation from '@/views/quality/infection-control/hai/view/LinkedIsolation.vue'
import CultureOrganism from '@/views/quality/infection-control/hai/view/CultureOrganism.vue'
import RecordMeta from '@/views/quality/infection-control/hai/view/RecordMeta.vue'
import { computeFlags, matchCriteria, expectedIsolationFor } from '@/views/quality/infection-control/hai/hai-derived'

definePage({
  meta: {
    subject: 'quality.infection_control.hai_case',
    action: 'view',
  },
})

const route = useRoute()
const router = useRouter()

const haiCase = ref(null)
const isActionLoading = ref(false)

const flags = computed(() => computeFlags(haiCase.value))

const onFlagJump = action => {
  if (action === 'isolation') {
    document.getElementById('hai-linked-isolation')?.scrollIntoView({ behavior: 'smooth', block: 'center' })

    return
  }

  const targetId = action === 'timeline' ? 'hai-device-timeline' : 'hai-case-details'

  document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const { execute: fetchCase } = await useApi(createUrl(`/infection-control/hai-cases/${route.params.id}`, {}), {
  afterFetch: ({ data }) => {
    haiCase.value = data
  },
  onFetchError: () => {
    router.push({ name: 'invalid-url', params: { code: 404 } })
  },
})

const refetchCase = () => fetchCase()

// --- CDC Criteria fetched from API ---
const allCriteriaForType = ref([])
const allExclusionsForType = ref([])

const fetchCriteriaForType = async haiType => {
  if (!haiType) {
    allCriteriaForType.value = []
    allExclusionsForType.value = []

    return
  }

  try {
    const { data: criteriaData } = await $api(`/masters/hai-type-criteria?hai_type=${haiType}&type=criterion&status=1`)

    allCriteriaForType.value = criteriaData ?? []
  } catch {
    allCriteriaForType.value = []
  }

  try {
    const { data: exclusionData } = await $api(`/masters/hai-type-criteria?hai_type=${haiType}&type=exclusion&status=1`)

    allExclusionsForType.value = exclusionData ?? []
  } catch {
    allExclusionsForType.value = []
  }
}

watch(() => haiCase.value?.hai_type, haiType => {
  fetchCriteriaForType(haiType)
}, { immediate: true })

const criteriaResult = computed(() => matchCriteria(allCriteriaForType.value, haiCase.value?.cdc_criteria_met))

// --- Confirm Dialog ---
const isConfirmDialogOpen = ref(false)

const openConfirmDialog = () => {
  isConfirmDialogOpen.value = true
}

const handleConfirmOnly = async () => {
  isActionLoading.value = true
  await HaiService.confirm(haiCase.value.id, { raise_incident: false }, () => {
    isConfirmDialogOpen.value = false
    refetchCase()
  }, () => {
    isActionLoading.value = false
  })
  isActionLoading.value = false
}

// --- Redirect Dialog ---
const isRedirectDialogOpen = ref(false)
const redirectCountdown = ref(3)
const createdIncidentId = ref(null)
let countdownTimer = null

const handleConfirmAndReport = async () => {
  isActionLoading.value = true
  await HaiService.confirm(haiCase.value.id, { raise_incident: true }, data => {
    isConfirmDialogOpen.value = false
    createdIncidentId.value = data.incident_id
    isRedirectDialogOpen.value = true
    startCountdown()
  }, () => {
    isActionLoading.value = false
  })
  isActionLoading.value = false
}

const startCountdown = () => {
  redirectCountdown.value = 3
  countdownTimer = setInterval(() => {
    redirectCountdown.value--
    if (redirectCountdown.value <= 0) {
      clearInterval(countdownTimer)
      goToIncident()
    }
  }, 1000)
}

const goToIncident = () => {
  if (countdownTimer) clearInterval(countdownTimer)
  isRedirectDialogOpen.value = false
  router.push({ name: 'app-quality-entry', params: { id: createdIncidentId.value } })
}

onBeforeUnmount(() => {
  if (countdownTimer) clearInterval(countdownTimer)
})

// --- Rule Out / Close ---
const confirmActionDialog = ref(false)
const pendingAction = ref(null)
const actionLabel = ref('')

const openAction = (action, label) => {
  pendingAction.value = action
  actionLabel.value = label
  confirmActionDialog.value = true
}

const executeAction = async () => {
  if (!pendingAction.value) return

  isActionLoading.value = true

  await pendingAction.value(haiCase.value.id, {}, () => {
    refetchCase()
    confirmActionDialog.value = false
  }, () => {
    isActionLoading.value = false
  })

  isActionLoading.value = false
}

// --- Isolation Drawer ---
const isIsolationDrawerOpen = ref(false)

const isolationPrefill = computed(() => haiCase.value
  ? { patient: haiCase.value.patient, organism: haiCase.value.organism, hai_case_id: haiCase.value.id }
  : null)

const canStartIsolation = computed(() =>
  [HaiCaseStatus.CONFIRMED, HaiCaseStatus.COLONIZATION].includes(haiCase.value?.status))

const hasActiveIsolation = computed(() =>
  (haiCase.value?.isolations ?? []).some(iso => iso.status === IsolationStatus.ACTIVE))

const closeBlocked = computed(() => Boolean(haiCase.value?.is_mdro) && hasActiveIsolation.value)

const expectedIsolationType = computed(() => expectedIsolationFor(haiCase.value))
</script>

<template>
  <section v-if="haiCase">
    <CaseHeaderCard
      :hai-case="haiCase"
      class="mb-6"
    />
    <VRow>
      <!-- Main Content -->
      <VCol
        cols="12"
        md="8"
        class="order-2 order-md-1"
      >
        <div class="d-flex flex-column gap-6">
          <!-- 1. Validation flags -->
          <ValidationFlags
            :flags="flags"
            :status="haiCase.status"
            @jump="onFlagJump"
          />

          <!-- 2. Device attribution timeline -->
          <DeviceTimeline
            v-if="haiCase.is_device_associated || haiCase.device_insertion_date || haiCase.device_removal_date"
            :hai-case="haiCase"
          />

          <!-- 3. Case details -->
          <CaseDetails
            :hai-case="haiCase"
            @edit="router.push({ name: 'app-quality-infection-control-hai-entry', params: { id: haiCase.id } })"
          />

          <!-- 4. CDC / NHSN criteria -->
          <CriteriaChecklist
            :criteria="allCriteriaForType"
            :exclusions="allExclusionsForType"
            :cdc-criteria-met="haiCase.cdc_criteria_met"
          />

          <!-- 5. Reviewer notes -->
          <ReviewerNotes :notes="haiCase.notes ?? ''" />
        </div>
      </VCol>

      <!-- Sidebar -->
      <VCol
        cols="12"
        md="4"
        class="order-1 order-md-2"
      >
        <div class="d-flex flex-column gap-4 hai-rail">
          <!-- MDRO alert -->
          <MdroAlert
            v-if="haiCase.is_mdro"
            :organism="haiCase.organism"
            :has-active-isolation="hasActiveIsolation"
            @review-precautions="isIsolationDrawerOpen = true"
          />

          <!-- Decision -->
          <DecisionCard
            :status="haiCase.status"
            :met-count="criteriaResult.metCount"
            :total="criteriaResult.total"
            :flag-count="flags.length"
            :close-blocked="closeBlocked"
            :loading="isActionLoading"
            @confirm="openConfirmDialog"
            @rule-out="openAction(HaiService.ruleOut.bind(HaiService), 'Rule Out HAI Case')"
            @close="openAction(HaiService.close.bind(HaiService), 'Close HAI Case')"
          />

          <!-- Linked isolation -->
          <LinkedIsolation
            :isolations="haiCase.isolations ?? []"
            :expected-type="expectedIsolationType"
            :can-start="canStartIsolation && !hasActiveIsolation"
            @start="isIsolationDrawerOpen = true"
          />

          <!-- Culture & organism -->
          <CultureOrganism :hai-case="haiCase" />

          <!-- Record -->
          <RecordMeta :hai-case="haiCase" />
        </div>
      </VCol>
    </VRow>
  </section>

  <!-- Confirm HAI Dialog (two options) -->
  <VDialog
    v-model="isConfirmDialogOpen"
    max-width="480"
    persistent
  >
    <VCard>
      <VCardItem class="py-3 px-4">
        <template #title>
          <div class="d-flex align-center gap-2">
            <IconBtn
              variant="tonal"
              color="success"
            >
              <VIcon
                icon="tabler-circle-check"
                size="22"
                color="success"
              />
            </IconBtn>
            <span class="text-h6 font-weight-medium">Confirm HAI Case</span>
          </div>
        </template>
        <template #append>
          <IconBtn @click="isConfirmDialogOpen = false">
            <VIcon icon="tabler-x" />
          </IconBtn>
        </template>
      </VCardItem>

      <VDivider />

      <VCardText class="text-body-1 pa-4">
        Are you sure you want to confirm this HAI case as a healthcare-associated infection?
      </VCardText>

      <VCardActions class="pa-4 pt-0">
        <VSpacer />
        <VBtn
          variant="outlined"
          color="secondary"
          :disabled="isActionLoading"
          @click="isConfirmDialogOpen = false"
        >
          Cancel
        </VBtn>

        <VBtn
          variant="outlined"
          color="success"
          :loading="isActionLoading"
          @click="handleConfirmOnly"
        >
          Confirm
        </VBtn>

        <VBtn
          variant="flat"
          color="success"
          :loading="isActionLoading"
          @click="handleConfirmAndReport"
        >
          Confirm & Report
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Redirect Countdown Dialog -->
  <VDialog
    v-model="isRedirectDialogOpen"
    max-width="420"
    persistent
  >
    <VCard class="text-center pa-6">
      <VIcon
        icon="tabler-circle-check"
        color="success"
        size="48"
        class="mb-4"
      />

      <VCardTitle class="text-h6 font-weight-medium">
        HAI Case Confirmed
      </VCardTitle>

      <VCardText class="text-body-1 mt-2">
        Incident created successfully. Redirecting to incident reporting in {{ redirectCountdown }}s...
      </VCardText>

      <VCardActions class="justify-center pt-4">
        <VBtn
          variant="flat"
          color="primary"
          @click="goToIncident"
        >
          Go Now
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Rule Out / Close Action Dialog -->
  <VDialog
    v-model="confirmActionDialog"
    max-width="420"
    persistent
  >
    <VCard>
      <VCardItem>
        <template #title>
          <span class="text-h6 font-weight-medium">{{ actionLabel }}</span>
        </template>
      </VCardItem>

      <VCardText class="text-body-1">
        Are you sure you want to {{ actionLabel.toLowerCase() }}? This will update the case status.
      </VCardText>

      <VCardActions class="pa-4 pt-0">
        <VSpacer />
        <VBtn
          variant="outlined"
          color="secondary"
          @click="confirmActionDialog = false"
        >
          Cancel
        </VBtn>

        <VBtn
          variant="flat"
          color="primary"
          :loading="isActionLoading"
          @click="executeAction"
        >
          Confirm
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <IsolationDrawer
    v-model:is-drawer-open="isIsolationDrawerOpen"
    :prefill="isolationPrefill"
    mode="create"
    @refetch-listing="refetchCase"
  />
</template>

<style lang="scss" scoped>
@media (min-width: 960px) {
  .hai-rail {
    position: sticky;
    inset-block-start: 1.5rem;
  }
}
</style>
